import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { graphql, useStaticQuery } from 'gatsby'
import React, { useMemo } from 'react'
import { PRIMARY_COLOR_MAPPING, SECONDARY_COLOR_MAPPING } from '../constants'

// Styled wrapper with enhanced styles
const HarborThemeWrapper = styled('div')(
  ({ linkColor, ctaPrimaryLinkColor, ctaSecondaryLinkColor }) => ({
    '& .withHarborThemeCta': {
      backgroundColor: ctaPrimaryLinkColor,
      color: ctaSecondaryLinkColor,
    },

    '& .withHarborThemeWrapper a': {
      color: linkColor,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
      '&:focus': { outline: '2px solid blue', outlineOffset: '2px' },
    },

    '& .withHarborThemeWrapper': {
      display: 'flex',
      flexWrap: 'wrap',
    },

    '& .withHarborThemeWrapper img': { maxWidth: '100%' },

    // NOTE: removed the stray comma after `ol` that broke this rule
    '& .withHarborThemeWrapper p:not(:empty), & .withHarborThemeWrapper ul, & .withHarborThemeWrapper ol': {
      width: '100%',
      fontFamily: 'Roboto',
      marginBottom: '8px',
    },

    '& .withHarborThemeWrapper span': { fontFamily: 'Roboto' },
    '& .withHarborThemeWrapper p:empty': { margin: 0, padding: 0 },

    '& .withHarborThemeWrapper h1, & .withHarborThemeWrapper h2, & .withHarborThemeWrapper h3, & .withHarborThemeWrapper h4, & .withHarborThemeWrapper h5, & .withHarborThemeWrapper h6': {
      width: '100%',
      fontFamily: 'Poppins',
      margin: '8px 0',
    },

    '& .withHarborThemeWrapper h1[class*="paint"], \
     & .withHarborThemeWrapper h2[class*="paint"], \
     & .withHarborThemeWrapper h3[class*="paint"], \
     & .withHarborThemeWrapper h4[class*="paint"], \
     & .withHarborThemeWrapper h5[class*="paint"], \
     & .withHarborThemeWrapper h6[class*="paint"]': {
      width: 'unset !important',
      display: 'inline !important',
    },

    '& .withHarborThemeWrapper hr': {
      width: '100%',
      height: '1px',
      color: 'rgb(9, 42, 72)',
    },

    /* --- Ordered list hierarchy: 1. / a. / i. / 1. --- */
    '& .withHarborThemeWrapper ol': {
      listStyleType: 'decimal',
      paddingLeft: '1.5rem',
      marginLeft: 0,
    },
    '& .withHarborThemeWrapper ol ol': {
      listStyleType: 'lower-alpha',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },
    '& .withHarborThemeWrapper ol ol ol': {
      listStyleType: 'lower-roman',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },
    '& .withHarborThemeWrapper ol ol ol ol': {
      listStyleType: 'decimal', // cycle back if you go this deep
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },

    /* --- Unordered list hierarchy for consistency --- */
    '& .withHarborThemeWrapper ul': {
      listStyleType: 'disc',
      paddingLeft: '1.5rem',
      marginLeft: 0,
    },
    '& .withHarborThemeWrapper ul ul': {
      listStyleType: 'circle',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },
    '& .withHarborThemeWrapper ul ul ul': {
      listStyleType: 'square',
      marginTop: '4px',
      paddingLeft: '1.5rem',
    },

    /* Optional: tighten spacing between list items for dense lists */
    '& .withHarborThemeWrapper li + li': {
      marginTop: '4px',
    },
  })
)


export const HarborCsThemeProvider = ({ children }) => {
  const data = useStaticQuery(graphql`
    query {
      contentstackTheme {
        default_link_color {
          link_color
        }
        default_cta_color {
          cta_color
        }
      }
    }
  `)

  const theme = useMemo(
    () => ({
      linkColor:
        PRIMARY_COLOR_MAPPING[
          data.contentstackTheme?.default_link_color?.link_color
        ] || PRIMARY_COLOR_MAPPING.defaultLinkColor,
      ctaPrimaryLinkColor:
        PRIMARY_COLOR_MAPPING[
          data.contentstackTheme?.default_cta_color?.cta_color
        ] || PRIMARY_COLOR_MAPPING.defaultCtaPrimaryColor,
      ctaSecondaryLinkColor:
        SECONDARY_COLOR_MAPPING[
          data.contentstackTheme?.default_cta_color?.cta_color
        ] || SECONDARY_COLOR_MAPPING.defaultCtaSecondaryColor,
    }),
    [data]
  )

  if (!data?.contentstackTheme) {
    console.warn('HarborCsThemeProvider query returned no data.')
    return <>{children}</> // Fallback rendering
  }

  return (
    <HarborThemeWrapper
      linkColor={theme.linkColor}
      ctaPrimaryLinkColor={theme.ctaPrimaryLinkColor}
      ctaSecondaryLinkColor={theme.ctaSecondaryLinkColor}
    >
      {children}
    </HarborThemeWrapper>
  )
}

HarborCsThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default HarborCsThemeProvider
